import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { ErrorState, LoadingState } from '@/components/states'
import { Container } from '@/components/layout'
import { useAuth } from './AuthProvider'
import { isAdmin, isModerator, useAccount } from './account'

/** Aceita só caminhos internos (evita redirecionamento aberto para outros sites). */
export function safeRedirect(value: string | null | undefined, fallback = '/'): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return fallback
  return value
}

export function useCurrentPath(): string {
  const location = useLocation()
  return `${location.pathname}${location.search}${location.hash}`
}

export function signInPath(redirect: string): string {
  return `/entrar?redirect=${encodeURIComponent(redirect)}`
}

export function onboardingPath(redirect: string): string {
  return `/cadastro?redirect=${encodeURIComponent(redirect)}`
}

/** Exige sessão; sem sessão, leva para /entrar e volta depois. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  const current = useCurrentPath()
  if (loading) return <LoadingState />
  if (!user) return <Navigate to={signInPath(current)} replace />
  return <>{children}</>
}

/** Exige cadastro concluído (nome público, idade e termos). Use dentro de RequireAuth. */
export function RequireOnboarded({ children }: { children: ReactNode }) {
  const { data: account, isPending, error, refetch } = useAccount()
  const current = useCurrentPath()
  if (isPending) return <LoadingState />
  if (error) {
    return (
      <Container className="py-12">
        <ErrorState error={error} onRetry={() => refetch()} />
      </Container>
    )
  }
  if (!account.onboarded) return <Navigate to={onboardingPath(current)} replace />
  return <>{children}</>
}

/** Exige papel de moderação ou administração. Use dentro de RequireAuth. */
export function RequireRole({ role, children }: { role: 'moderator' | 'admin'; children: ReactNode }) {
  const { data: account, isPending, error, refetch } = useAccount()
  if (isPending) return <LoadingState />
  if (error) {
    return (
      <Container className="py-12">
        <ErrorState error={error} onRetry={() => refetch()} />
      </Container>
    )
  }
  const allowed = role === 'admin' ? isAdmin(account) : isModerator(account)
  if (!allowed) return <Navigate to="/" replace />
  return <>{children}</>
}
